/**
 * ATM Menu Navigation System for Shopify
 *
 * Manages navigation between ATM screens for different ATM types.
 */

document.addEventListener('DOMContentLoaded', function () {
  const ATMNavigation = (function () {
    const domElements = {
      mainStage: document.querySelector('#atm-mainStage'),
      groupHeader: document.querySelector('#atm-group'),
      contentArea: document.querySelector('#atm-content'),
      selectionScreen: document.querySelector('#atm-firstSelectStage'),
      loader: document.getElementById('atm-loader'),
      progressBar: document.getElementById('atm-progress-bar'),
      progressText: document.getElementById('atm-progress-text'),
      genmegaButton: document.getElementById('atm-genmegaHantle'),
      hyosungButton: document.getElementById('atm-hyosung'),
      homeBtn: document.getElementById('atm-homeBtn'),
    };

    const ATM_START_PAGES = {
      'GENMEGA/HANTLE': '01 - Error Code Screen',
      HYOSUNG: 'HY - Operation Program',
    };

    const appState = {
      currentPageTitle: ATM_START_PAGES['GENMEGA/HANTLE'],
      imageCache: {},
      atmType: 'GENMEGA/HANTLE',
      loadingProgress: 0,
      pagesData: null,
      preloadedTypes: {},
    };

    function updateProgressBar(percent) {
      const boundedPercent = Math.min(Math.max(percent, 0), 100);
      domElements.progressBar.style.width = `${boundedPercent}%`;
      domElements.progressText.textContent = `${Math.round(boundedPercent)}%`;
      appState.loadingProgress = boundedPercent;
    }

    function loadATMData() {
      const dataUrl = "//atmtrader.com/cdn/shop/t/103/assets/atm-menu-navigation-data.json?v=110454464542084133501791391063";
      const dataField = document.querySelector('script[type="application/json"][data-atm-data]');

      if (dataField && dataField.textContent) {
        try {
          appState.pagesData = JSON.parse(dataField.textContent);
          return Promise.resolve(appState.pagesData);
        } catch (error) {
          console.error('Error parsing embedded ATM data:', error);
        }
      }

      return fetch(dataUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to load ATM data');
          }
          return response.json();
        })
        .then((data) => {
          appState.pagesData = data;
          return data;
        })
        .catch((error) => {
          console.error('Error loading ATM data:', error);
          return null;
        });
    }

    /**
     * Shopify assets are flat filenames (no folders). Map stored paths to asset names.
     */
    function getAssetUrl(filename) {
      if (filename.startsWith('http') || filename.startsWith('//')) {
        return filename;
      }

      const shopifyName = filename
        .replace(/^hyosung\//, 'hyosung-')
        .replace(/\//g, '-')
        .replace(/ /g, '-');

      const baseUrl = "//atmtrader.com/cdn/shop/t/103/assets/empty";
      return 'https:' + baseUrl.replace('empty', '') + shopifyName;
    }

    function preloadImages(atmType) {
      if (!appState.pagesData) {
        updateProgressBar(100);
        return Promise.resolve();
      }

      const atmPages = appState.pagesData[atmType];
      if (!atmPages || !atmPages.length) {
        console.error(`No pages found for ATM type: ${atmType}`);
        updateProgressBar(100);
        return Promise.resolve();
      }

      if (appState.preloadedTypes[atmType]) {
        updateProgressBar(100);
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        let loadedCount = 0;
        const totalImages = atmPages.length;
        updateProgressBar(0);

        const finishOne = () => {
          loadedCount++;
          updateProgressBar((loadedCount / totalImages) * 100);
          if (loadedCount === totalImages) {
            appState.preloadedTypes[atmType] = true;
            resolve();
          }
        };

        atmPages.forEach((page) => {
          const cacheKey = `${atmType}::${page.title}`;
          if (appState.imageCache[cacheKey]) {
            finishOne();
            return;
          }

          const img = new Image();
          img.src = getAssetUrl(page.url);
          img.onload = () => {
            appState.imageCache[cacheKey] = img;
            finishOne();
          };
          img.onerror = () => {
            console.error(`Failed to load image: ${page.url}`);
            finishOne();
          };
        });
      });
    }

    function findPage(title) {
      if (!appState.pagesData) return null;
      appState.currentPageTitle = title;
      return appState.pagesData[appState.atmType].find((page) => page.title === title) || null;
    }

    function displayPage(pageTitle) {
      const page = findPage(pageTitle);
      if (!page) {
        console.error(`Page not found: ${pageTitle}`);
        return;
      }

      domElements.mainStage.innerHTML = '';
      domElements.groupHeader.innerHTML = '';
      domElements.contentArea.innerHTML = '';

      const cacheKey = `${appState.atmType}::${page.title}`;
      const img = appState.imageCache[cacheKey];
      if (img) {
        domElements.mainStage.appendChild(img);
      } else {
        const fallback = new Image();
        fallback.src = getAssetUrl(page.url);
        fallback.onerror = () => console.error(`Failed to load image: ${page.url}`);
        domElements.mainStage.appendChild(fallback);
      }

      if (page.group) {
        domElements.groupHeader.innerHTML = page.group;
      }

      if (page.btns && page.btns.length) {
        createPageButtons(page.btns);
      }

      if (page.content) {
        domElements.contentArea.innerHTML = page.content;
      }

      if (page.contentImg) {
        addContentImage(page.contentImg);
      }

      setupEventListeners();
    }

    function createPageButtons(buttons) {
      buttons.forEach((btn) => {
        const button = document.createElement('div');
        button.style.position = 'absolute';
        button.style.left = btn.left + '%';
        button.style.top = btn.top + '%';
        button.style.width = btn.width + '%';
        button.style.height = btn.height + '%';
        button.style.zIndex = '1';
        button.style.cursor = 'pointer';
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          displayPage(btn.to);
        });
        domElements.mainStage.appendChild(button);
      });
    }

    function addContentImage(contentImgUrl) {
      const contentImg = new Image();
      contentImg.src = getAssetUrl(contentImgUrl);
      contentImg.style.width = '300px';
      contentImg.onerror = () => console.error(`Failed to load content image: ${contentImgUrl}`);
      domElements.contentArea.appendChild(contentImg);
    }

    function setupEventListeners() {
      document.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('click', handleClick);
      document.addEventListener('keydown', handleKeyPress);
      document.addEventListener('click', handleClick);
    }

    function handleKeyPress(event) {
      if (!appState.pagesData) return;

      const currentPage = appState.pagesData[appState.atmType].find(
        (page) => page.title === appState.currentPageTitle
      );
      if (!currentPage?.keys) return;

      const keyAction = currentPage.keys.find(
        (k) => k.key.toLowerCase() === event.key.toLowerCase()
      );

      if (keyAction) {
        displayPage(keyAction.to);
      }
    }

    function handleClick() {
      if (!appState.pagesData) return;

      const currentPage = appState.pagesData[appState.atmType].find(
        (page) => page.title === appState.currentPageTitle
      );
      if (!currentPage?.click) return;
      displayPage(currentPage.click);
    }

    function showSelectionScreen() {
      domElements.mainStage.innerHTML = '';
      domElements.groupHeader.innerHTML = '';
      domElements.contentArea.innerHTML = '';
      domElements.mainStage.style.display = 'none';
      domElements.groupHeader.style.display = 'none';
      domElements.contentArea.style.display = 'none';
      domElements.homeBtn.style.display = 'none';
      domElements.selectionScreen.style.display = 'flex';
    }

    function showAtmStage() {
      domElements.selectionScreen.style.display = 'none';
      domElements.mainStage.style.display = 'flex';
      domElements.groupHeader.style.display = 'flex';
      domElements.contentArea.style.display = 'block';
      domElements.homeBtn.style.display = 'block';
    }

    async function selectAtm(atmType) {
      if (!appState.pagesData || !appState.pagesData[atmType]) {
        console.error(`Unknown ATM type: ${atmType}`);
        return;
      }

      appState.atmType = atmType;
      appState.currentPageTitle = ATM_START_PAGES[atmType];

      if (!appState.preloadedTypes[atmType]) {
        domElements.loader.style.display = 'flex';
        updateProgressBar(0);
        await preloadImages(atmType);
        await new Promise((resolve) => setTimeout(resolve, 300));
        domElements.loader.style.display = 'none';
      }

      showAtmStage();
      displayPage(appState.currentPageTitle);
    }

    function goHome() {
      showSelectionScreen();
    }

    function initializeApp() {
      domElements.loader.style.display = 'flex';
      updateProgressBar(0);

      loadATMData().then(() => {
        preloadImages('GENMEGA/HANTLE').then(() => {
          setTimeout(() => {
            domElements.loader.style.display = 'none';
          }, 400);
        });
      });

      domElements.genmegaButton.addEventListener('click', () => {
        selectAtm('GENMEGA/HANTLE');
      });

      if (domElements.hyosungButton) {
        domElements.hyosungButton.addEventListener('click', () => {
          selectAtm('HYOSUNG');
        });
      }

      domElements.homeBtn.addEventListener('click', goHome);
    }

    return {
      init: initializeApp,
    };
  })();

  ATMNavigation.init();
});
